import type { AggregationFilterOptionByObjectType, ClientQueryDataConfigGetObjectsResponse, ClientQueryDataConfigObjectVersion } from '../../store/engine/types';
import type { ServiceAttributesById, Services } from '../../store/engine/types/schema';
import { AGGREGATION_SERVICE_KEY_IN_SERVICES } from 'flow/service/services/constants';

const AGGREGATION_OBJECT_TYPE_BY_ATTRIBUTE_NAME = {
  entities: 'entity',
};

const helpers = {
  convertToAggregationFilterOptionsByObjectType: ({
    clientQueryDataConfigObjects,
  }: {
    clientQueryDataConfigObjects: ClientQueryDataConfigGetObjectsResponse['data_objects'];
  }): AggregationFilterOptionByObjectType => clientQueryDataConfigObjects
    .reduce((acc, configObject) => {
      const activeConfigObjectVersion = helpers.getActiveConfigObjectVersion({
        configObjectVersions: configObject.versions,
      });

      if (!activeConfigObjectVersion || !activeConfigObjectVersion?.columns) {
        return acc;
      }

      const aggregationFilters = activeConfigObjectVersion.columns
        .filter(column => column.user_filterable)
        .map(column => ({
          label: column.display_name,
          value: column.column_slug,
        }));
      acc[configObject.object_slug] = aggregationFilters;
      return acc;
    }, {} as AggregationFilterOptionByObjectType),

  formatAggregationServiceAttributesById: ({
    aggregationFilterOptionsByObjectType,
    aggregationServiceAttributesById,
  }: {
    aggregationFilterOptionsByObjectType: AggregationFilterOptionByObjectType;
    aggregationServiceAttributesById: ServiceAttributesById;
  }): ServiceAttributesById => Object.fromEntries(Object
    .entries(
      aggregationServiceAttributesById,
    )
    .map(([attributeId, attribute]) => {
      const objectType = AGGREGATION_OBJECT_TYPE_BY_ATTRIBUTE_NAME[attribute.attribute_name];

      if (!objectType) {
        return [attributeId, attribute];
      }

      return [
        attributeId,
        {
          ...attribute,
          aggregationFilterOptions: (
            aggregationFilterOptionsByObjectType[objectType]
          ),
        },
      ];
    }),
  ),

  getActiveConfigObjectVersion: ({
    configObjectVersions,
  }: {
    configObjectVersions: ClientQueryDataConfigObjectVersion[];
  }): ClientQueryDataConfigObjectVersion | undefined => (
    configObjectVersions.find(version => version.active_selects)
  ),

  getAggregationServiceId: (
    { servicesById }: { servicesById: Services },
  ): number | undefined => Object
    .values(servicesById)
    .find(service => (
      service.internal_name === AGGREGATION_SERVICE_KEY_IN_SERVICES),
    )
    ?.id,
};

export const addAggregationFilterOptionsToAggregationsService = ({
  clientQueryDataConfigObjects,
  servicesById,
}: {
  clientQueryDataConfigObjects: ClientQueryDataConfigGetObjectsResponse['data_objects'];
  servicesById: Services;
}): Services => {
  const aggregationsServiceId = helpers.getAggregationServiceId({ servicesById });

  if (!aggregationsServiceId) {
    return servicesById;
  }

  const aggregationServiceAttributesById = servicesById[aggregationsServiceId].attributes;

  if (!aggregationServiceAttributesById) {
    return servicesById;
  }

  const aggregationFilterOptionsByObjectType = helpers
    .convertToAggregationFilterOptionsByObjectType({
      clientQueryDataConfigObjects,
    });

  const aggregationServiceAttributesWithFilterOptionsById = helpers
    .formatAggregationServiceAttributesById({
      aggregationFilterOptionsByObjectType,
      aggregationServiceAttributesById,
    });

  return {
    ...servicesById,
    [aggregationsServiceId]: {
      ...servicesById[aggregationsServiceId],
      attributes: aggregationServiceAttributesWithFilterOptionsById,
    },
  };
};
